// src/features/chat/components/PendingAssistantBubble.jsx

import React from 'react';
import RecordCitationMarkdown from './RecordCitationMarkdown';
import { SenderMeta } from './SenderMeta';
import messageStyles from '../styles/Message.module.css';

/**
 * Renders the pending assistant response bubble during streaming.
 *
 * Isolated from the message list to prevent re-renders during streaming.
 *
 * @param {Object} props
 * @param {Object|null} props.pending - Pending assistant state
 * @param {string} props.botIcon - Bot avatar image source
 * @param {string} props.botName - Bot display name
 */
export function PendingAssistantBubble({ pending, botIcon, botName }) {
  if (!pending) return null;

  const { phase, text, recordCitations = [] } = pending;
  const isThinking = phase === 'thinking' && !text;
  const isStreaming = phase === 'streaming' || (phase === 'thinking' && text);
  const isError = phase === 'error';

  return (
    <div
      className={messageStyles.row}
      data-variant="received"
      data-has-avatar="true"
    >
      <SenderMeta
        sender={{ displayName: botName, avatarUrl: botIcon, isAssistant: true }}
        botIcon={botIcon}
        botName={botName}
      />

      <div
        className={[
          messageStyles.bubble,
          isThinking ? messageStyles.typingBubble : '',
          isError ? messageStyles.errorBubble : '',
          isStreaming ? messageStyles.streaming : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Message content */}
        {isThinking ? (
          <div className={messageStyles.typingIndicator}>
            <span />
            <span />
            <span />
          </div>
        ) : isError ? (
          <span>Something went wrong. Please try again.</span>
        ) : (
          <>
            <RecordCitationMarkdown
              text={text}
              recordCitations={recordCitations}
            />

            {/* Streaming cursor */}
            {isStreaming && (
              <span className={messageStyles.streamingCursor} aria-hidden="true">
                ▊
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PendingAssistantBubble;
